import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { LoadingPanel } from '../../components/ui/Loader';
import { Modal } from '../albums/Modal';
import { messages } from './api';

/**
 * Sending a track to somebody, from wherever the track is.
 *
 * ## Why this is not "copy a link"
 *
 * §6.5 calls a track shared into a message the single highest-intent discovery
 * path there is - a friend sending you a song converts better than any ranking
 * - and a copied link is the version of that which loses the thread. It leaves
 * the app, arrives somewhere we cannot render it, and the person receiving it
 * has to decide to come back. Sent as an attachment it stays a real reference:
 * the conversation shows a card that plays, and a track later made private
 * stops resolving rather than rotting into a dead URL.
 *
 * ## Existing conversations only, for now
 *
 * The list is who you are already talking to. Starting a new conversation from
 * here would need a people-search inside a dialog opened from a track, which is
 * two searches deep from where somebody started - and `messages.start` already
 * exists on a profile for the case where you want to reach somebody new.
 *
 * ## It sends and closes, with no message box
 *
 * The attachment is the message. An optional note would be one more thing to
 * decide before sending, on a control whose whole value is that it is quick,
 * and the conversation is right there to type into afterwards.
 */
export function SendTrackDialog({
  trackId,
  title,
  onClose,
}: {
  trackId: number;
  title: string;
  onClose: () => void;
}) {
  const [sent, setSent] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const inbox = useQuery({
    queryKey: ['conversations'],
    queryFn: () => messages.inbox(),
  });

  const send = useMutation({
    mutationFn: (conversationId: number) => messages.send(conversationId, { track_id: trackId }),
    onSuccess: (_result, conversationId) => setSent((done) => [...done, conversationId]),
    onError: () => setError('That did not send. Try again in a moment.'),
  });

  const conversations = inbox.data?.items ?? [];

  return (
    <Modal title={`Send “${title}”`} onClose={onClose}>
      {error && <Alert tone="error">{error}</Alert>}

      {inbox.isPending ? (
        <LoadingPanel label="Loading conversations" />
      ) : conversations.length === 0 ? (
        <p className="text-sm text-soft">
          You have no conversations yet. Open somebody’s profile and message them, then you can
          send them tracks from here.
        </p>
      ) : (
        <ul className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto">
          {conversations.map((conversation) => {
            const done = sent.includes(conversation.id);
            const name =
              conversation.other?.display_name ?? conversation.title ?? 'Conversation';

            return (
              <li key={conversation.id}>
                <button
                  type="button"
                  data-send-to={conversation.id}
                  /*
                   * Stays disabled once sent rather than resetting.
                   *
                   * The dialog can stay open to send the same track to several
                   * people, and without this the only way to know whether the
                   * third tap registered is to go and look at the thread.
                   */
                  disabled={done || send.isPending}
                  onClick={() => {
                    setError(null);
                    send.mutate(conversation.id);
                  }}
                  className="flex w-full items-center gap-3 rounded-[var(--radius-control)] px-3 py-2.5 text-left transition-colors hover:bg-raised disabled:cursor-default disabled:opacity-60"
                >
                  {conversation.other?.avatar ? (
                    <img
                      src={conversation.other.avatar}
                      alt=""
                      className="h-8 w-8 shrink-0 rounded-[var(--radius-pill)] object-cover"
                    />
                  ) : (
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-raised text-xs font-bold text-soft">
                      {name.charAt(0).toUpperCase()}
                    </span>
                  )}

                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{name}</span>

                  <span className="meta-caps shrink-0 text-faint">{done ? 'Sent' : 'Send'}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
